import { useState } from 'react'
import { Edit2, Trash2 } from 'lucide-react'
import { useBookmarkStore } from '../store/bookmarkStore'
import { Button } from './Button'
import { ConfirmDialog } from './ConfirmDialog'
import { Category } from '../types/bookmark'

export function CategoryManager() {
  const { categories, bookmarks, addCategory, removeCategory, updateCategory } = useBookmarkStore()
  const [showForm, setShowForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingCategory) {
      updateCategory(editingCategory.id, formData)
      setEditingCategory(null)
    } else {
      addCategory(formData)
    }
    setFormData({ name: '', description: '' })
    setShowForm(false)
  }

  const handleEdit = (category: Category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name,
      description: category.description || '',
    })
    setShowForm(true)
  }

  const getBookmarkCount = (categoryId: string) => {
    return bookmarks.filter(bookmark => bookmark.categoryId === categoryId).length
  }

  return (
    <div className="space-y-4">
      {/* 添加按钮 */}
      <div className="flex justify-end">
        <Button
          onClick={() => {
            setEditingCategory(null)
            setFormData({ name: '', description: '' })
            setShowForm(true)
          }}
        >
          添加分类
        </Button>
      </div>

      {/* 分类表单 */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-lg font-medium mb-4">
              {editingCategory ? '编辑分类' : '添加分类'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  名称
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                  描述
                </label>
                <textarea
                  id="description"
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowForm(false)
                    setEditingCategory(null)
                  }}
                  type="button"
                >
                  取消
                </Button>
                <Button type="submit">
                  {editingCategory ? '更新' : '添加'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 删除确认对话框 */}
      {categoryToDelete && (
        <ConfirmDialog
          title="删除分类"
          message={`确定要删除分类"${categoryToDelete.name}"吗？该分类下的${getBookmarkCount(categoryToDelete.id)}个书签将被移至"无分类"。`}
          onConfirm={() => {
            removeCategory(categoryToDelete.id)
            setCategoryToDelete(null)
          }}
          onCancel={() => setCategoryToDelete(null)}
        />
      )}

      {/* 分类列表 */}
      <div className="bg-white shadow rounded-lg divide-y">
        {categories.map((category) => (
          <div key={category.id} className="p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-medium text-gray-900">
                  {category.name}
                </h3>
                {category.description && (
                  <p className="mt-1 text-sm text-gray-500">
                    {category.description}
                  </p>
                )}
                <div className="mt-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {getBookmarkCount(category.id)} 个书签
                  </span>
                </div>
              </div>
              <div className="ml-4 flex-shrink-0 flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(category)}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCategoryToDelete(category)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {categories.length === 0 && (
          <div className="p-4 text-center text-gray-500">
            暂无分类，点击上方按钮添加
          </div>
        )}
      </div>
    </div>
  )
} 